import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import App from "./App";
import "./index.css";
import "./i18n";
import { initializeTheme } from "./store/theme";
import { unlockAudio } from "./utils/notificationSound";
import { registrarServiceWorker } from "./pwa/registerSW";
import { deveGuardar, persister } from "./pwa/queryPersister";

// Initialize theme before rendering
initializeTheme();

// Antes do React: o primeiro `controllerchange` do service worker chega cedo e
// sem ninguém escutando ele se perde.
registrarServiceWorker();

// Libera o áudio no primeiro toque/clique em qualquer lugar da página. iOS e
// Chrome exigem um gesto do usuário para o AudioContext sair de "suspended", e
// até aqui o único lugar que cumpria esse requisito era o botão de som do
// sininho — quem nunca o abriu nunca ouviu o aviso de mensagem nova.
["pointerdown", "touchstart"].forEach((evento) =>
  window.addEventListener(evento, () => unlockAudio(), { once: true, passive: true }),
);

// Cache agressivo: a maioria das listagens (users, squads, agents,
// tags) não mudam frequentemente, então mantemos os dados em memória
// 30 minutos e não revalidamos no foco da janela. Mutations
// relevantes invalidam manualmente via queryClient.invalidateQueries.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,    // 5min sem refetch automático
      gcTime: 30 * 60 * 1000,      // 30min em cache após desmontar
      refetchOnWindowFocus: false, // não refetch ao voltar pra aba
      refetchOnReconnect: "always",
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        // Um retrato de ontem ainda ajuda a abrir o app; mais velho que isso
        // atrapalha mais do que informa.
        maxAge: 24 * 60 * 60 * 1000,
        // Todo deploy descarta o cache gravado: formato de resposta muda entre
        // versões, e um retrato antigo hidratado dentro de componente novo
        // quebra de um jeito difícil de diagnosticar.
        buster: __BUILD_ID__,
        dehydrateOptions: { shouldDehydrateQuery: deveGuardar },
      }}
    >
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
